import React, { useState } from 'react';
import { buildCountryDisplay, CountryDisplay } from '../utils/country';
import { getCountryNameByCode } from '../utils/countryLookup';
import { getBadgeIcon } from '../utils/badgeIcons';
import { formatEarnedDate, formatPeriodMetadata } from '../utils/badgeFormatting';
import BadgeDetailModal from './BadgeDetailModal';

export interface Badge {
  id: string;
  name: string;
  description: string;
  iconUrl: string | null;
  category: string;
  rarity: string;
  criteriaType: string;
  criteriaValue: string | null;
  level: string | null;
}

export interface UserBadge {
  id: string;
  userId: string;
  badgeId: string;
  earnedAt: string;
  isFeatured: boolean;
  currentLevel: string | null;
  metadata?: Record<string, any> | null;
  badge?: Badge;
}

interface BadgeDisplayProps {
  badge: Badge;
  level?: string | null;
  size?: 'sm' | 'md' | 'lg';
  showDescription?: boolean;
  className?: string;
  isUnlocked?: boolean; // If false, badge is locked (not earned yet)
  earnedAt?: string | null; // When the badge was earned
  metadata?: Record<string, any> | null; // Badge metadata (e.g., period information for leaderboard badges)
  customText?: string; // Custom text to replace the badge name
  showMeta?: boolean; // Show period and earned details
  interactive?: boolean; // Enables click-to-open modal
}

const BadgeDisplay: React.FC<BadgeDisplayProps> = ({
  badge,
  level,
  size = 'md',
  showDescription = false,
  className,
  isUnlocked = true,
  earnedAt = null,
  metadata = null,
  customText,
  showMeta = false,
  interactive = true
}) => {
  const isEarned = isUnlocked;
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const resolvedLevel = level ?? badge.level ?? null;
  const classes = [
    'badge-display',
    `badge-display--${size}`,
    `badge-display--${badge.rarity}`,
    resolvedLevel ? `badge-display--level-${resolvedLevel.toLowerCase()}` : '',
    !isEarned ? 'badge-display--locked' : '',
    showDescription ? 'badge-display--show-description' : '',
    showMeta ? 'badge-display--show-meta' : '',
    interactive ? 'badge-display--interactive' : '',
    className
  ].filter(Boolean).join(' ');

  // Format level display
  const levelDisplay = resolvedLevel ? (
    <span className="badge-display__level">{resolvedLevel}</span>
  ) : null;

  const earnedDateStr = earnedAt ? formatEarnedDate(earnedAt) : null;
  const periodStr = formatPeriodMetadata(metadata);
  const isLeaderboardBadge = badge.category === 'leaderboard';
  const isCountryChampion = badge.name === 'Country Champion';
  
  // Get country information from metadata for Country Champion badges
  // Try multiple sources: metadata, then try to build display from available info
  const countryCode = metadata?.country_code || null;
  const countryName = metadata?.country_name || (countryCode ? getCountryNameByCode(countryCode) : null);
  
  // Always try to build country display if we have any country info
  let countryDisplay: CountryDisplay | null = null;
  if (countryCode || countryName) {
    countryDisplay = buildCountryDisplay(countryCode, countryName);
  }
  
  // For Country Champion badges, always try to show a flag if we have any country info
  const hasCountryInfo = Boolean(countryCode || countryName);
  
  // Format badge name with country if it's a Country Champion badge, or use customText if provided
  const displayName = customText
    ? customText
    : isCountryChampion && countryName 
      ? `${countryName} Champion`
      : badge.name;
  
  // Format badge description with country name if it's a Country Champion badge
  const displayDescription = isCountryChampion && countryName
    ? badge.description.replace(/your country/gi, countryName)
    : badge.description;
  
  // Get SVG icon component for badge
  const BadgeIconComponent = getBadgeIcon(badge.name);
  
  // Determine icon size based on badge display size
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 32 : 24;
  
  // Helper function to render Country Champion flag
  const renderCountryFlag = () => {
    if (!isCountryChampion) return null;
    
    // Try to get country display - use existing or build from code/name
    let display: CountryDisplay | null = countryDisplay;
    if (!display && hasCountryInfo) {
      display = buildCountryDisplay(countryCode, countryName);
    }
    
    // If still no display, try building with just code or just name
    if (!display) {
      if (countryCode) {
        display = buildCountryDisplay(countryCode, null);
      } else if (countryName) {
        display = buildCountryDisplay(null, countryName);
      }
    }
    
    if (!display) {
      return null;
    }
    
    const iso2 = display.iso2?.toLowerCase();
    const hasFlagEmoji = Boolean(display.flag);
    const flagImgSrc = iso2 ? `https://flagicons.lipis.dev/flags/4x3/${iso2}.svg` : null;
    
    // Prioritize flag emoji if available, otherwise use flag image
    if (hasFlagEmoji && display.flag) {
      return (
        <span className="badge-display__icon-flag" role="img" aria-label={display.ariaLabel}>
          {display.flag}
        </span>
      );
    } else if (flagImgSrc && iso2) {
      return (
        <img 
          className="badge-display__icon-flag-img"
          src={flagImgSrc}
          alt={display.ariaLabel || countryName || 'Country flag'}
          loading="lazy"
        />
      );
    }
    
    return null;
  };

  // Determine what to render in the icon area
  const renderIcon = () => {
    // For Country Champion badges, always try to show flag first
    if (isCountryChampion) {
      const flagResult = renderCountryFlag();
      if (flagResult) return flagResult;
      // If flag rendering failed, fall through to show placeholder
    }
    
    // For other badges or if flag failed, show iconUrl, SVG icon, or placeholder
    if (badge.iconUrl) {
      return <img src={badge.iconUrl} alt={badge.name} />;
    }
    
    if (BadgeIconComponent) {
      return <BadgeIconComponent size={iconSize} className="badge-display__icon-svg" color="currentColor" />;
    }
    
    return (
      <span className="badge-display__icon-placeholder">
        {badge.name.charAt(0).toUpperCase()}
      </span>
    );
  };

  const handleOpenDetails = () => {
    if (!interactive) return;
    setIsDetailOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!interactive) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsDetailOpen(true);
    }
  };

  return (
    <>
      <div
        className={classes}
        onClick={handleOpenDetails}
        onKeyDown={handleKeyDown}
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
      >
      <div className="badge-display__icon">
        {renderIcon()}
      </div>
      <div className="badge-display__content">
        <div className="badge-display__name">
          {displayName}
          {levelDisplay}
        </div>
        {showDescription && (
          <div className="badge-display__description">{displayDescription}</div>
        )}
        {showMeta && isEarned && isLeaderboardBadge && periodStr && (
          <div className="badge-display__period">Period: {periodStr}</div>
        )}
        {showMeta && isEarned && earnedDateStr && (
          <div className="badge-display__earned-date">Earned {earnedDateStr}</div>
        )}
      </div>
      </div>

      <BadgeDetailModal
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        badge={badge}
        level={resolvedLevel}
        isUnlocked={isUnlocked}
        earnedAt={earnedAt}
        metadata={metadata}
      />
    </>
  );
};

export default BadgeDisplay;

